import $ from 'jquery'
import { get, post, patch, getSkipCache } from 'base/lib/network'
import apiUrl from 'base/api_url'
import Event from 'base/event'
import { getChosenShippingOption } from 'base/reducers/checkout/shippingOptions'
import isEmpty from 'lodash/isEmpty'
import Bugsnag from '@bugsnag/js'

import {
  startRequest,
  blockCart,
  resetLineItems,
  unblockCart,
  enableCart,
  finishRequest,
  fireEvent,
  resetUser,
  resetVisitor,
  resetCart,
  receiveOrderPreview,
  receiveShippingOptions,
  receiveOrder,
  receiveAddress,
  receiveAddresses,
  receiveCreditCards,
  receiveFactories,
  resetBraintreeToken,
  receiveCollection,
  receivePDPCollection,
  receiveProducts,
  receiveSimilarProducts,
  receiveStyledProducts,
  receiveRecentlyViewedProducts,
  removeAddress,
  setPaymentMethod,
  setNewQuantityForReturnUnit,
  receiveBuilderMenus,
  receiveBuilderMenusV2,
  receiveSidenavMenus,
  receiveModal,
  receivePage,
  setWaitlistReservation,
  setSelectedProduct,
  setEmailSubscription,
  receiveEmailSubscription,
  updateVisitor,
  updateShippingPolicy,
  receiveAppConfig,
  receiveShippingConstants,
  updateRegions,
  receiveProductPrices,
  removeAddressesFromSession,
  removeCreditCardsFromSession,
  receiveInitialSession,
  receiveReturnPolicy,
  receiveProductUpsell,
  receiveLineItem,
} from 'base/actions/synchronous'

import { getLocalization, getShippingCountry } from 'base/lib/localization'

/*

   This file contains all of the asynchronous actions (AKA "thunks") in our codebase.
   You can think of these as the "processes" that the website needs to complete. For example:
   - Creating an order
   - Signing in
   - Adding a line item to your cart

   Each of these processes requires multiple state changes to complete, and often one or more
   calls over the network. Thunks give us a way to trigger multiple state changes over a period
   of time. For more information on how thunks work, refer to:

   https://github.com/reactjs/redux/blob/master/docs/advanced/AsyncActions.md#async-action-creators

 */

/*
   AUTHENTICATION
   ==============
 */

// Create a new user session and store the user data in the application store.
// NOTE: also entirely replaces the cart (read: line items) with the user's previous cart state.
export function signIn(data, { siteLocation }) {
  return async dispatch => {
    dispatch(startRequest())
    const timestamp = Date.now().toString()
    const { session } = await post(apiUrl('login'), {
      ...data,
      timestamp,
      site_location: siteLocation,
    })
    const eventData = {
      ...session.user,
      timestamp,
      siteLocation,
    }

    await dispatch(receiveInitialSession(session))
    await dispatch(fetchCart(session.cart))
    dispatch(updateVisitor(session?.visitor))
    dispatch(resetUserAndAddresses(session.user))
    dispatch(fetchCreditCards())

    dispatch(finishRequest())
    dispatch(fireEvent(Event.User.SIGN_IN, eventData))
  }
}

// Create a new user and session and store the new user data in the application store.
export function createAccount(userData, { siteLocation }) {
  return dispatch => {
    dispatch(startRequest())
    const timestamp = Date.now().toString()
    return post(apiUrl('users'), {
      user: userData,
      site_location: siteLocation,
      timestamp,
    }).then(({ session }) => {
      const eventData = {
        ...session.user,
        timestamp,
        siteLocation,
      }
      dispatch(fetchCart(session.cart))
      dispatch(resetUserAndAddresses(session.user))
      dispatch(finishRequest())

      dispatch(fireEvent(Event.User.SIGN_UP, eventData))
      dispatch(fireEvent(Event.User.SIGN_IN, eventData))
    })
  }
}

// Note that if a user is on a page that requires a user (for instance, checkout review page)
// and then logs out, the page may error due to expected user state not existing. To avoid this
// problem, it is good practice to `routeTo` the homepage before dispatching this action.
export function logOut(options = { redirectToHomepage: true }) {
  return (dispatch, getState) => {
    const isFbConnected = getState().session.user.facebook_connected

    dispatch(startRequest())
    return post(apiUrl('logout')).then(() => {
      if (window.FB && isFbConnected) {
        window.FB.logout()
      }

      if (options.redirectToHomepage) {
        window.location.href = '/'
      }
      dispatch(resetUser(null))
      dispatch(setPaymentMethod(null))
      dispatch(removeAddressesFromSession())
      dispatch(removeCreditCardsFromSession())
      dispatch(resetLineItems([]))
      dispatch(resetBraintreeToken())
      dispatch(finishRequest())
      dispatch(fireEvent(Event.User.SIGN_OUT))
    })
  }
}

/*
  USER / VISITOR
  ==============
*/

// Helper function to keep addresses out of user state
export function resetUserAndAddresses(user) {
  return dispatch => {
    const { ...userWithoutAddresses } = user
    dispatch(resetUser(userWithoutAddresses))
    dispatch(resetBraintreeToken())
    dispatch(fetchAddresses())
  }
}

// Helper function to keep addresses out of visitor state
export function resetVisitorAndAddresses(visitor) {
  return dispatch => {
    const { addresses, ...visitorWithoutAddresses } = visitor
    dispatch(resetVisitor(visitorWithoutAddresses))
    dispatch(receiveAddresses(addresses))
    dispatch(resetBraintreeToken())
  }
}

export function fetchUser() {
  return (dispatch, getState) => {
    dispatch(startRequest())
    const { user } = getState().session

    return get(apiUrl(`users/${user.guid}`)).then(newUser => {
      const selectedPaymentForm = getState().checkout.paymentMethod

      dispatch(setPaymentMethod(selectedPaymentForm))
      dispatch(resetUserAndAddresses(newUser))
      dispatch(finishRequest())
    })
  }
}

export function saveVisitor(newAttributes) {
  return (dispatch, getState) => {
    dispatch(startRequest())
    return patch(apiUrl(`visitors/${getState().session.visitor.session_id}`), newAttributes).then(
      visitor => {
        dispatch(
          resetVisitorAndAddresses({
            ...getState().session.visitor,
            ...visitor,
          }),
        )
        dispatch(finishRequest())
      },
    )
  }
}

export function saveEmailSubscription(email, email_type, attributes) {
  return dispatch => {
    dispatch(startRequest())
    return post(apiUrl('email_subscriptions'), {
      email_subscription: {
        email,
        email_type,
        ...attributes,
      },
    }).then(subscription => {
      dispatch(setEmailSubscription(subscription.email_subscription))
      dispatch(finishRequest())
    })
  }
}

export function fetchEmailSubscription(email) {
  if (!email) {
    return
  }

  return dispatch => {
    dispatch(startRequest())
    return post(apiUrl('email_subscriptions/find_by_email'), {
      email_subscription: { email },
    }).then(subscriptions => {
      dispatch(receiveEmailSubscription(subscriptions.email_subscription))
      dispatch(finishRequest())
    })
  }
}

export function saveUser(newUserAttributes = {}) {
  return (dispatch, getState) => {
    dispatch(startRequest())
    const { user } = getState().session
    return patch(apiUrl(`users/${user.guid}`), { user: newUserAttributes }).then(newUser => {
      dispatch(resetUserAndAddresses(newUser))

      dispatch(finishRequest())
    })
  }
}

export function sendPasswordResetEmail(email) {
  return dispatch => {
    dispatch(startRequest())
    return post(apiUrl('password_resets'), { password_resets: { email } }).then(response => {
      dispatch(finishRequest())
      return response
    })
  }
}

export function resetPassword(token, password, siteLocation) {
  return dispatch => {
    dispatch(startRequest())
    return post(apiUrl(`password_resets/${token}`), {
      password_resets: { password },
    }).then(response => {
      const eventData = { ...response.user, site_location: siteLocation }

      if (response.user) {
        dispatch(fetchCart(response.cart))

        dispatch(resetUserAndAddresses(response.user))

        dispatch(fireEvent(Event.User.SIGN_IN, eventData))
      }

      dispatch(finishRequest())

      return response
    })
  }
}

export function updateVisitorOrUserAndEmailSubscriptions({ attributes = {}, email, email_type }) {
  return dispatch => {
    dispatch(startRequest())

    return get(apiUrl('users/availability'), {
      email,
    }).then(response => {
      if (response.is_available) {
        dispatch(saveVisitor({ email, gender: attributes.gender }))
      }
      dispatch(saveEmailSubscription(email, email_type, attributes))
      dispatch(finishRequest())
    })
  }
}

export function fetchCreditCards() {
  return async (dispatch, getState) => {
    const { session: { user } = {} } = getState() || {}

    if (!user) return null

    dispatch(startRequest())
    const creditCards = await get(apiUrl('credit_cards'))
    dispatch(receiveCreditCards(creditCards))
    dispatch(finishRequest())
  }
}

export function createLineItem(lineItemData) {
  return async function (dispatch) {
    try {
      dispatch(startRequest())
      const response = await get(apiUrl('line_items/new'), { ...lineItemData })
      dispatch(receiveLineItem(response))
      dispatch(finishRequest())
    } catch (error) {
      Bugsnag.notify(error, event => {
        event.addMetadata('CreateLineItemError', error)
      })
    }
  }
}

export function fetchAddresses() {
  return async dispatch => {
    dispatch(startRequest())
    const addresses = await get(apiUrl('addresses'))
    dispatch(receiveAddresses(addresses))
    dispatch(finishRequest())
  }
}

export function createAddress(newAddress) {
  return dispatch => {
    dispatch(startRequest())
    return post(apiUrl('addresses'), newAddress).then(({ data }) => {
      dispatch(updateShippingPolicy(data.shipping_policy))
      dispatch(receiveAddress(data))
      dispatch(
        fireEvent(Event.Checkout.SUBMIT_ADDRESS, {
          address_id: data.id,
        }),
      )
      dispatch(finishRequest())
      return data
    })
  }
}

export function updateAddress(address, siteLocation) {
  const oldAddressId = address.id
  return dispatch => {
    dispatch(startRequest())
    return patch(apiUrl(`addresses/${oldAddressId}`), {
      edit: true,
      address: address,
      site_location: siteLocation,
    }).then(({ data }) => {
      dispatch(updateShippingPolicy(data.shipping_policy))
      dispatch(receiveAddress(data))
      dispatch(removeAddress(oldAddressId))
      dispatch(finishRequest())
    })
  }
}

/*
  CART
  ====
*/
export function fetchCart(newCart, skipCache = false) {
  return (dispatch, getState) => {
    const cart = newCart || getState().cart
    const fetchMethod = skipCache ? getSkipCache : get

    return fetchMethod(apiUrl(`carts/${cart.id}`)).then(({ cart }) => {
      dispatch(resetCart(cart))
      dispatch(enableCart())
      dispatch(finishRequest())
    })
  }
}

/*
  ORDERS
  ======
*/

// Fetch a specific order
export function fetchOrder(orderNumber, { token } = {}) {
  return dispatch => {
    dispatch(startRequest())

    return get(apiUrl(`orders/${orderNumber}`), { token }).then(order => {
      dispatch(receiveOrder(order))
      dispatch(finishRequest())
    })
  }
}

// Note that this request requires a shipping option. The shippingOptions reducer
// should guarantee that an option is selected at any given time.
export function fetchOrderPreview(
  overrideData = null,
  options = { hasCarbonOffset: false, cart: false },
) {
  return (dispatch, getState) => {
    const state = getState()
    let address,
      selectedShippingOption = null
    if (overrideData) {
      address = overrideData.address
      selectedShippingOption = overrideData.shippingOption
    } else {
      address = state.cart?.shipping_address

      selectedShippingOption = getChosenShippingOption(state.checkout.shippingOptions)
    }

    dispatch(startRequest())
    return post(apiUrl('orders/preview'), {
      address,
      fulfillment_method_key: selectedShippingOption.key,
      has_carbon_offset: options.hasCarbonOffset,
      include_promos_in_subtotal: options.include_promos_in_subtotal,
      cart: options.cart,
    }).then(orderPreview => {
      dispatch(receiveOrderPreview(orderPreview))
      dispatch(finishRequest())
    })
  }
}

// Given a return authorization and a return unit ID, cancel any return units that have not been
// received by the warehouse.
export function cancelRemainingReturnUnits(returnAuthorization, returnUnitId) {
  return dispatch => {
    dispatch(startRequest())

    const url = `orders/${returnAuthorization.orderNumber}/cancel_return_unit/${returnUnitId}`

    return post(apiUrl(url)).then(({ new_quantity }) => {
      dispatch(
        setNewQuantityForReturnUnit({
          returnAuthorization,
          returnUnitId,
          newQuantity: new_quantity,
        }),
      )
      dispatch(finishRequest())
    })
  }
}

export function fetchShippingOptionsWithLocation(location) {
  return (dispatch, getState) => {
    const state = getState()
    if (!isEmpty(state.checkout.shippingOptions)) return

    dispatch(startRequest())

    const address = state.cart.shipping_address

    const params = {
      cart: { id: state.cart.id },
      address: address || location,
    }
    // so that we don't need a street address to fetch options
    if (!address) {
      params.without_street_address = true
    }

    return get(apiUrl('shipping_options'), params).then(({ shipping_options }) => {
      dispatch(receiveShippingOptions(shipping_options))
      dispatch(finishRequest())
    })
  }
}

export function fetchShippingOptions() {
  return (dispatch, getState) => {
    const state = getState()
    dispatch(startRequest())

    return get(apiUrl('shipping_options'), {
      cart: { id: state.cart.id },
      address: state.cart.shipping_address,
    }).then(({ shipping_options }) => {
      dispatch(receiveShippingOptions(shipping_options))
      dispatch(finishRequest())
    })
  }
}

// NOTE: In a world where "quantity" is simply multiple line items with the same variant, this
// action should instead create a new line item or remove an existing one.
export function updateItemQuantity(lineItem, newQuantity) {
  return (dispatch, getState) => {
    const { cart } = getState()

    if (newQuantity !== 0) {
      dispatch(
        fireEvent(Event.Checkout.CHANGE_PRODUCT_QUANTITY, {
          product_id: lineItem.product_id,
          unit_change: newQuantity - lineItem.quantity,
        }),
      )
    }

    if (!cart.isBlocked) {
      dispatch(startRequest())
      dispatch(blockCart())

      return patch(apiUrl(`line_items/${lineItem.id}`), {
        quantity: newQuantity,
      }).then(response => {
        dispatch(resetCart(response.cart))
        dispatch(unblockCart())
        dispatch(finishRequest())
      })
    }

    return Promise.reject('Cart is already being modified')
  }
}

export function updateGiftcard(lineItem, unit) {
  const unit_attributes = {
    recipient_email: unit?.recipientEmail,
    recipient_name: unit?.recipientName,
    notify_at: new Date(unit?.deliveryDate).toUTCString(),
    message: unit?.message,
  }

  return (dispatch, getState) => {
    const { cart } = getState(0)
    if (!cart.isBlocked) {
      dispatch(startRequest())
      dispatch(blockCart())

      return patch(apiUrl(`line_items/${lineItem.id}`), {
        unit_attributes,
      }).then(response => {
        dispatch(resetLineItems(response.cart.line_items))
        dispatch(unblockCart())
        dispatch(finishRequest())
      })
    }
  }
}

/*
  OTHER
  =====
*/
export function fetchCollection(permalink) {
  return (dispatch, getState) => {
    if (permalink in getState().collections) {
      return new $.Deferred().resolve()
    }

    dispatch(startRequest())
    return get(apiUrl(`collections/${permalink}`)).then(collection => {
      dispatch(receiveCollection(collection))
      dispatch(finishRequest())
    })
  }
}

export function fetchCollectionV3(permalink) {
  return async (dispatch, getState) => {
    if (permalink in getState().collections) return

    dispatch(startRequest())
    const collection = await get(apiUrl(`collections/${permalink}`, { version: 3 }))

    dispatch(receiveCollection(collection))
    dispatch(finishRequest())
  }
}

export function fetchProductPrices(productIds) {
  return async (dispatch, getState) => {
    if (productIds.length === 0) {
      return new $.Deferred().resolve()
    }

    dispatch(startRequest())

    let productPrices
    const { cart } = getState()
    const localization = getLocalization()
    const productPriceRequests = []

    for (const id of productIds) {
      const request = get(apiUrl(`products/${id}/price`), {
        country: getShippingCountry([], cart.shipping_address),
        currency: localization.currency,
      })
      productPriceRequests.push(request)
    }

    productPrices = await Promise.all(productPriceRequests).then(responses => {
      const products = responses.map(response => {
        return response.products[0]
      })

      return {
        currency_code: responses[0].currency_code,
        currency_symbol: responses[0].currency_symbol,
        products,
      }
    })

    dispatch(receiveProductPrices(productPrices))
    dispatch(finishRequest())
  }
}

const fetchedProductGroupIds = new Map()

export function fetchProductGroupPrices(productGroupId) {
  return async (dispatch, getState) => {
    const { cart } = getState()
    const localization = getLocalization()
    const country = getShippingCountry([], cart.shipping_address)
    const currency = localization.currency
    const key = `${country}-${currency}`

    // If our "cache store" has values for a _different_ country + currency
    // combination, blow it away because the user has changed country + currency
    if (!fetchedProductGroupIds.get(key) && fetchedProductGroupIds.size) {
      fetchedProductGroupIds.clear()
    }

    // Initialize an empty set for this country + currency if we haven't created
    // it yet.
    if (!fetchedProductGroupIds.has(key)) {
      fetchedProductGroupIds.set(key, new Set())
    }

    const fetchedIdsForKey = fetchedProductGroupIds.get(key)

    if (!fetchedIdsForKey.has(productGroupId)) {
      fetchedIdsForKey.add(productGroupId)
    } else {
      return
    }

    const productGroupPrices = await get(apiUrl(`product_groups/${productGroupId}/prices`), {
      country,
      currency,
    })

    dispatch(receiveProductPrices(productGroupPrices))
  }
}

export function fetchPDPCollection(permalink) {
  return async (dispatch, getState) => {
    if (permalink in getState().pdpCollections) return

    dispatch(startRequest())
    const collection = await get(apiUrl(`collections/${permalink}`, { version: 3 }), {
      context: 'product_page',
    })
    dispatch(receivePDPCollection(collection))
    dispatch(finishRequest())
  }
}

export function fetchProductGroup(productIdentifier) {
  return dispatch => {
    dispatch(startRequest())
    return get(apiUrl('product_groups'), {
      product_permalink: productIdentifier,
    })
      .then(productGroup => {
        const selectedProduct = productGroup.products.find(product => {
          return [product.id, product.permalink].includes(productIdentifier)
        })
        const productGroupId = selectedProduct.product_group_id

        return dispatch(fetchProductGroupPrices(productGroupId)).then(() => {
          dispatch(receiveProducts(productGroup))
          dispatch(setSelectedProduct(selectedProduct))
        })
      })
      .always(() => dispatch(finishRequest()))
  }
}

export function fetchSimilarProducts(productPermalink) {
  return async dispatch => {
    dispatch(startRequest())

    const response = await get(apiUrl(`products/${productPermalink}/similar_products`))
    const productIds = response.similar_products.map(product => product.id)

    dispatch(fetchProductPrices(productIds))
    dispatch(receiveSimilarProducts(productPermalink, response.similar_products))
    dispatch(finishRequest())
  }
}

export function fetchRecentlyViewedProducts(productIds) {
  return async dispatch => {
    dispatch(startRequest())

    const response = await get(apiUrl(`products/${productIds}/recently_viewed`))

    dispatch(fetchProductPrices(productIds))
    dispatch(receiveRecentlyViewedProducts(response.recently_viewed.products))
    dispatch(finishRequest())
  }
}

export function fetchStyledProducts(productPermalink) {
  return dispatch => {
    dispatch(startRequest())

    return get(apiUrl(`products/${productPermalink}/styled_products`)).then(response => {
      dispatch(receiveStyledProducts(productPermalink, response.styled_products))
      dispatch(finishRequest())
    })
  }
}

export function fetchModal(platform) {
  return dispatch => {
    dispatch(startRequest())

    const country_code = getLocalization().country_code

    return get(apiUrl('modals', { version: 3 }), {
      platform,
      country_code,
    }).then(modal => {
      dispatch(receiveModal(modal))
      dispatch(finishRequest())
    })
  }
}

export function setWaitlistReservationOnUser({ variant_id, product_id, collection_id, email }) {
  return (dispatch, getState) => {
    dispatch(startRequest())
    return post(apiUrl('waitlist_reservations'), {
      waitlist_reservation: {
        variant_id,
        product_id,
        collection_id,
        email,
      },
    }).then(waitlistReservation => {
      dispatch(setWaitlistReservation(waitlistReservation))
      if (getState().session.user) {
        dispatch(fetchUser())
      }
      dispatch(finishRequest())
    })
  }
}

export function fetchPage(permalink) {
  return dispatch => {
    dispatch(startRequest())
    return get(apiUrl(`pages/${permalink}`))
      .then(page => {
        dispatch(receivePage(page))
      })
      .always(() => dispatch(finishRequest()))
  }
}

export function refreshPrices() {
  return (dispatch, getState) => {
    const products = getState().productPrices.products
    if (products) {
      const productIds = products.map(prd => prd.id)
      return dispatch(fetchProductPrices(productIds))
    }
  }
}

export function geolocateVisitor(localeOverride = null) {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    const localization = await get(apiUrl(`visitors/geolocate`), {
      locale_override: localeOverride,
    })
    const oldLocalization = getLocalization()

    if (oldLocalization && oldLocalization.currency !== localization.currency) {
      dispatch(refreshPrices())
      dispatch(fetchCart())
      dispatch(fetchOrderPreview())

      if (getState().session.user) {
        dispatch(fetchUser())
      }
    }

    dispatch(updateVisitor({ localization }))
    dispatch(updateShippingPolicy(localization.shipping_policy))
    dispatch(finishRequest())
  }
}

export function changeGeolocation(countryCode) {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    const localization = await post(apiUrl(`visitors/change_geolocation`), {
      country_code: countryCode,
    })
    const oldLocalization = getLocalization()

    await dispatch(updateVisitor({ localization }))

    if (oldLocalization && oldLocalization.country_code !== localization.country_code) {
      dispatch(refreshPrices())
      dispatch(fetchCart())
      dispatch(fetchOrderPreview())

      if (getState().session.user) {
        dispatch(fetchUser())
      }
    }

    dispatch(updateShippingPolicy(localization.shipping_policy))
    dispatch(finishRequest())
  }
}

export function fetchRegions() {
  return dispatch => {
    dispatch(startRequest())

    return get(apiUrl('regions'), { only_shipping_countries: 'true' }).then(response => {
      dispatch(updateRegions(response))
      dispatch(finishRequest())
    })
  }
}

export function fetchAppConfig() {
  return async dispatch => {
    dispatch(startRequest())

    const data = await get(apiUrl('app_configs'))

    dispatch(receiveAppConfig(data))
    dispatch(finishRequest())
  }
}

export function fetchShippingConstants() {
  return async (dispatch, getState) => {
    if (!isEmpty(getState().shippingConstants)) return
    dispatch(startRequest())

    const data = await get(apiUrl('shipping_constants'))
    dispatch(receiveShippingConstants(data))
    dispatch(finishRequest())
  }
}

export function fetchProductUpsell() {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    const url = new URL('https://cdn.builder.io/api/v3/content/product-upsell')
    url.searchParams.set('apiKey', getState().appConfig.builderApiKey)
    url.searchParams.set('cacheSeconds', 300)
    url.searchParams.set('userAttributes.foo', 'bar') // workaround to respect scheduling until Builder patches this bug

    try {
      const productUpsellPromise = await fetch(url)
      const productUpsell = await productUpsellPromise?.json()
      productUpsell.results.sort((a, b) => a.data.position - b.data.position)
      const productUpsellPermalinks = productUpsell.results.map(
        result => result.data.link.split('/')[1],
      )
      const productGroupPromises = []
      productUpsellPermalinks.forEach(permalink =>
        productGroupPromises.push(
          get(apiUrl('product_groups'), {
            product_permalink: permalink,
          }),
        ),
      )

      const upsellProductGroups = await Promise.all(productGroupPromises)
      dispatch(receiveProductUpsell(upsellProductGroups, productUpsellPermalinks))
    } catch (e) {
      // no-op
    } finally {
      dispatch(finishRequest())
    }
  }
}

export function fetchSidenavMenus() {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    const url = new URL('https://cdn.builder.io/api/v3/content/menu')
    url.searchParams.set('apiKey', getState().appConfig.builderApiKey)
    url.searchParams.set('cacheSeconds', 0)
    url.searchParams.set('cacheBust', true)
    url.searchParams.set('userAttributes.foo', 'bar') // workaround to respect scheduling until Builder patches this bug

    const builderMenus = await fetch(url).then(menus => menus.json())

    dispatch(receiveSidenavMenus(builderMenus))
    dispatch(finishRequest())
  }
}

export function fetchMenus({ isIntl = false, version = 1 } = {}) {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    if (version === 1) {
      const endpoint = isIntl
        ? 'https://cdn.builder.io/api/v3/content/intl-menu'
        : 'https://cdn.builder.io/api/v3/content/menu'

      const url = new URL(endpoint)
      url.searchParams.set('apiKey', getState().appConfig.builderApiKey)
      url.searchParams.set('cacheSeconds', 0)
      url.searchParams.set('cacheBust', true)
      url.searchParams.set('userAttributes.foo', 'bar') // workaround to respect scheduling until Builder patches this bug

      const builderMenus = await fetch(url).then(menus => menus.json())
      dispatch(receiveBuilderMenus(builderMenus))
    }

    if (version === 2) {
      const endpoint = 'https://cdn.builder.io/api/v3/content/menu-v-2'

      const url = new URL(endpoint)
      url.searchParams.set('apiKey', getState().appConfig.builderApiKey)
      url.searchParams.set('cacheSeconds', 300)
      url.searchParams.set('userAttributes.domesticCustomer', !isIntl.toString())
      url.searchParams.set('userAttributes.foo', 'bar') // workaround to respect scheduling until Builder patches this bug

      const builderMenus = await fetch(url).then(menus => menus.json())
      dispatch(receiveBuilderMenusV2(builderMenus))
    }

    dispatch(finishRequest())
  }
}

export function fetchSession() {
  const sessionParams = new URLSearchParams()

  return async dispatch => {
    dispatch(startRequest())

    const session = await get(apiUrl(`session?${sessionParams.toString()}`))

    dispatch(receiveInitialSession(session))
    dispatch(finishRequest())
  }
}

export function fetchReturnPolicy(countryCode) {
  return async dispatch => {
    dispatch(startRequest())

    const returnPolicy = await get(apiUrl(`countries/${countryCode}/return_policy`))
    dispatch(receiveReturnPolicy(returnPolicy))

    dispatch(finishRequest())
  }
}

/*
   FACTORIES
   ==============
 */
export function fetchFactories() {
  return async (dispatch, getState) => {
    dispatch(startRequest())

    const url = new URL('https://cdn.builder.io/api/v3/content/factory')
    url.searchParams.set('apiKey', getState().appConfig.builderApiKey)
    url.searchParams.set('cacheSeconds', 300)
    url.searchParams.set('limit', 100)
    const factories = await fetch(url).then(menus => menus.json())

    dispatch(receiveFactories(factories))
    dispatch(finishRequest())
  }
}
